import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Tag, Row, Col, Dropdown, Table, Input, Select, Spin, Empty, message } from "antd";
import {
  MoreOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  LinkOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { PageHeader, ProjectsWrapper, ProjectTableWrapper } from "./Projects.style";
import axios from "axios";
import { API_CONST } from "../../../const";
import { PermissionGate } from "../../../components/PermissionGate/PermissionGate";
import { usePermissions } from "../../../contexts/PermissionContext";

const { Option } = Select;

const statusColors = {
  pending_approval: "orange",
  active: "green",
  rejected: "red",
  completed: "default",
};

const statusLabels = {
  pending_approval: "Pending Approval",
  active: "Active",
  rejected: "Rejected",
  completed: "Completed",
};

const formatCurrency = (value, showZero = false) => {
  if (value === null || value === undefined || value === "") return "-";
  if (value === 0 && showZero) return "₹ 0";
  if (!value) return "-";
  return `₹ ${parseFloat(value).toLocaleString("en-IN")}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const CustomerProjects = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [viewMode, setViewMode] = useState("grid");
  const [activeTab, setActiveTab] = useState("all");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [stats, setStats] = useState({
    total: 0,
    pendingApproval: 0,
    active: 0,
    completed: 0,
    rejected: 0,
  });

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const status = activeTab === "all" ? undefined : activeTab;
      
      const response = await axios.post(
        API_CONST.GET_PROJECTS,
        { status, search: searchText, limit: 100 },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setProjects(response.data.projects || []);
    } catch (error) {
      console.error("Error fetching projects:", error);
      message.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchText]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_PROJECT_STATS,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data.stats || {});
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchStats();
  }, [fetchProjects, fetchStats]);

  const handleViewDetails = (id) => {
    navigate(`/customer/project/${id}`);
  };

  const handleEditProject = (id) => {
    navigate(`/customer/edit-project/${id}`);
  };

  const getActionMenuItems = (project) => {
    const items = [];
    
    if (hasPermission('project.view')) {
      items.push({
        key: "view",
        label: "View Details",
        icon: <EyeOutlined />,
        onClick: () => handleViewDetails(project.id),
      });
    }

    if ((project.status === "pending_approval" || project.status === "rejected") && hasPermission('project.edit')) {
      items.push({
        key: "edit",
        label: project.status === "rejected" ? "Edit & Resubmit" : "Edit",
        icon: <EditOutlined />,
        onClick: () => handleEditProject(project.id),
      });
    }

    return items;
  };

  const getHealthBadge = (health, type) => {
    if (!health) return null;
    const status = health[type];
    return (
      <span className={`health-badge ${status}`}>
        {status === "good" && <CheckCircleOutlined />}
        {status === "warning" && <ClockCircleOutlined />}
        {status === "critical" && <ClockCircleOutlined />}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const tableColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 150,
      render: (id) => <span style={{ fontSize: 12, color: "#8c8c8c" }}>{id}</span>,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title, record) => (
        <span className="project-title-link" onClick={() => handleViewDetails(record.id)}>
          {title}
        </span>
      ),
    },
    {
      title: "Engagement",
      dataIndex: "engagementId",
      key: "engagementId",
      width: 160,
      render: (engagementId) => (
        <span 
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/customer/engagement/${engagementId}`)}
        >
          {engagementId}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => <Tag color={statusColors[status]}>{statusLabels[status]}</Tag>,
    },
    {
      title: "Budget Health",
      key: "budgetHealth",
      width: 130,
      render: (_, record) => record.status === "active" ? getHealthBadge(record.health, "budget") : "-",
    },
    {
      title: "Budget",
      dataIndex: "budgetAllocated",
      key: "budgetAllocated",
      width: 150,
      render: (budget) => formatCurrency(budget),
    },
    {
      title: "Jobs",
      dataIndex: "jobCount",
      key: "jobCount",
      width: 80,
      align: "center",
    },
    {
      title: "Start Date",
      dataIndex: "expectedStartDate",
      key: "expectedStartDate",
      width: 120,
      render: (date) => formatDate(date),
    },
    {
      title: "Rejection Reason",
      dataIndex: "rejectionReason",
      key: "rejectionReason",
      width: 200,
      render: (reason, record) => 
        record.status === "rejected" && reason ? (
          <span style={{ color: "#cf1322" }}>{reason}</span>
        ) : "-",
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <Dropdown menu={{ items: getActionMenuItems(record) }} trigger={["click"]}>
          <MoreOutlined style={{ cursor: "pointer", fontSize: 18 }} />
        </Dropdown>
      ),
    },
  ];

  return (
    <ProjectsWrapper>
      <PageHeader>
        <h1>My Projects</h1>
        <p>Manage and track your projects</p>
      </PageHeader>
      <Row gutter={16} className="metrics-row">
        <Col xs={24} sm={6}>
          <Card className="metric-card" hoverable onClick={() => setActiveTab("active")}>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#f6ffed" }}>
                <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{stats.active || 0}</h3>
                <p className="metric-label">Active Projects</p>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="metric-card" hoverable onClick={() => setActiveTab("pending_approval")}>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#fff7e6" }}>
                <ClockCircleOutlined style={{ color: "#fa8c16", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{stats.pendingApproval || 0}</h3>
                <p className="metric-label">Pending Approval</p>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="metric-card" hoverable>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#e6f7ff" }}>
                <TeamOutlined style={{ color: "#1890ff", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{stats.avgTalentsPerProject || 0}</h3>
                <p className="metric-label">Avg Talents/Project</p>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card className="metric-card" hoverable>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#f0f5ff" }}>
                <DollarOutlined style={{ color: "#597ef7", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{formatCurrency(stats.avgSpentPerProject, true)}</h3>
                <p className="metric-label">Avg Spent/Project</p>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <div className="tabs-section">
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            className={`tab-button ${activeTab === "all" ? "active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            All ({stats.total || 0})
          </Button>
          <Button
            className={`tab-button ${activeTab === "pending_approval" ? "active" : ""}`}
            onClick={() => setActiveTab("pending_approval")}
          >
            Pending ({stats.pendingApproval || 0})
          </Button>
          <Button
            className={`tab-button ${activeTab === "active" ? "active" : ""}`}
            onClick={() => setActiveTab("active")}
          >
            Active ({stats.active || 0})
          </Button>
          <Button
            className={`tab-button ${activeTab === "completed" ? "active" : ""}`}
            onClick={() => setActiveTab("completed")}
          >
            Completed ({stats.completed || 0})
          </Button>
          <Button
            className={`tab-button ${activeTab === "rejected" ? "active" : ""}`}
            onClick={() => setActiveTab("rejected")}
          >
            Rejected ({stats.rejected || 0})
          </Button>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div className="view-toggle">
            <Button
              className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
              icon={<AppstoreOutlined />}
              onClick={() => setViewMode("grid")}
            />
            <Button
              className={`toggle-btn ${viewMode === "table" ? "active" : ""}`}
              icon={<UnorderedListOutlined />}
              onClick={() => setViewMode("table")}
            />
          </div>
          <PermissionGate permission="project.create">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/customer/create-project")}
            >
              Create Project
            </Button>
          </PermissionGate>
        </div>
      </div>

      <div className="filters-section">
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search projects..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
      </div>

      <Spin spinning={loading}>
        {viewMode === "grid" ? (
          <div className="projects-grid">
            {projects.length === 0 && !loading ? (
              <Card className="empty-card">
                <Empty description="No projects found" />
              </Card>
            ) : (
              projects.map((proj) => (
                <Card key={proj.id} className="project-card" hoverable>
                  <div className="card-header">
                    <div>
                      <p className="project-id">{proj.id}</p>
                      <h3 className="project-title" onClick={() => handleViewDetails(proj.id)}>
                        {proj.title}
                      </h3>
                    </div>
                    <Dropdown menu={{ items: getActionMenuItems(proj) }} trigger={["click"]}>
                      <MoreOutlined className="action-icon" />
                    </Dropdown>
                  </div>

                  <div className="card-meta">
                    <Tag color={statusColors[proj.status]}>{statusLabels[proj.status]}</Tag>
                  </div>

                  <div 
                    className="engagement-link"
                    onClick={() => navigate(`/customer/engagement/${proj.engagementId}`)}
                  >
                    <LinkOutlined />
                    <span>Engagement: {proj.engagementId}</span>
                  </div>

                  <div style={{ marginTop: 8, fontSize: 13, color: "#014c75", fontWeight: 500 }}>
                    Budget:{" "}
                    {proj.budgetAllocated != null && Number(proj.budgetAllocated) > 0
                      ? `₹${Number(proj.budgetAllocated).toLocaleString("en-IN")}`
                      : "₹0"}
                  </div>

                  {proj.description && <p className="card-description">{proj.description}</p>}

                  {proj.status === "rejected" && proj.rejectionReason && (
                    <div className="rejection-reason" style={{ 
                      background: "#fff2f0", 
                      border: "1px solid #ffccc7", 
                      borderRadius: 6, 
                      padding: "8px 12px", 
                      marginTop: 12 
                    }}>
                      <strong style={{ color: "#cf1322" }}>Rejection Reason:</strong>
                      <p style={{ margin: "4px 0 0", color: "#595959" }}>{proj.rejectionReason}</p>
                    </div>
                  )}

                  {proj.status === "active" && proj.health && (
                    <div className="health-indicators">
                      <div className="health-item">
                        <span className="health-label">Timeline:</span>
                        <span className={`health-value ${proj.health.timeline}`}>
                          {proj.health.timeline === "good" && "On Track"}
                          {proj.health.timeline === "warning" && "Ending Soon"}
                          {proj.health.timeline === "critical" && "Overdue"}
                        </span>
                      </div>
                      <span className="health-separator">|</span>
                      <div className="health-item">
                        <span className="health-label">Budget:</span>
                        <span className={`health-value ${proj.health.budget}`}>
                          {proj.health.budget === "good" && "OK"}
                          {proj.health.budget === "warning" && "80%+"}
                          {proj.health.budget === "critical" && "Over"}
                        </span>
                      </div>
                      <span className="health-separator">|</span>
                      <div className="health-item">
                        <span className="health-label">Jobs:</span>
                        <span className="health-jobs-value">{proj.jobCount || 0}</span>
                      </div>
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>
        ) : (
          <ProjectTableWrapper>
            <Table
              columns={tableColumns}
              dataSource={projects}
              rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
              scroll={{ x: 1100 }}
              locale={{ emptyText: <Empty description="No projects found" /> }}
            />
          </ProjectTableWrapper>
        )}
      </Spin>
    </ProjectsWrapper>
  );
};

export default CustomerProjects;
